import { SECURITY_TYPE_ALIAS, UNDERLYING_SECURITY_ALIAS } from 'common/constants/cap-table/capTable';

// ONE source of truth for the AI field-name translation, shared by the parser
// (attaching ai_finding metadata to grid cells) and securityAiPatches (store
// accept/dismiss patches). Kept as a LEAF module - parser.ts drags the heavy
// `utilities` barrel (ESM deps jest cannot transform), which would poison every
// light consumer/test of the patches.
//
// ai_finding.field_metadata is keyed by Security MODEL field names (the backend's
// AI_FIELD_TO_SECURITY_FIELD in cap_table_generation_job_service.py), while grid
// rows use CELL aliases. For most fields the two coincide; the exceptions below
// must be resolved or their findings never attach to a cell (no AI rendering, no
// metadata-backed review card) and store patches silently miss. Review flows keep
// SENDING cell aliases unchanged - the backend accepts both synonyms.
export const AI_METADATA_KEY_BY_ALIAS: Record<string, string> = {
  [SECURITY_TYPE_ALIAS]: 'security_type_id',
  [UNDERLYING_SECURITY_ALIAS]: 'underlying_security',
};

// Inverse view: metadata key -> the Security field the grid actually READS (the
// parser reads column[row.alias]). Writing an accepted value onto the metadata
// key itself (e.g. `underlying_security`) is invisible to the grid.
export const SECURITY_FIELD_BY_AI_METADATA_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(AI_METADATA_KEY_BY_ALIAS).map(([alias, key]) => [key, alias])
);

// Canonical metadata key for a field name arriving as EITHER a cell alias or a
// metadata key (identity for every unmapped field).
export const aiMetadataKeyOf = (fieldName: string): string => AI_METADATA_KEY_BY_ALIAS[fieldName] ?? fieldName;

// Citation/mapping references have the same key-vs-alias split, plus a third
// spelling: rows written straight from the AI results carry the RAW camelCase
// data-point name (e.g. field_name='underlyingSecurity' observed live), which
// matches NO row alias on any FE. Accept every known synonym when keying a
// column's references to a row; everything the FE SENDS stays the cell alias.
export const REFERENCE_FIELD_SYNONYMS_BY_ALIAS: Record<string, string[]> = {
  [SECURITY_TYPE_ALIAS]: ['security_type_id', 'securityType'],
  [UNDERLYING_SECURITY_ALIAS]: ['underlying_security', 'underlyingSecurity'],
};

// Every spelling a reference's field_name may use for the given field names
// (alias, model key, raw camelCase) - so a decline keyed by EITHER spelling
// still hits refs stored under any of them.
export const referenceFieldNameSet = (fieldNames: string[]): Set<string> => {
  const names = new Set(fieldNames);
  fieldNames.forEach(fieldName => {
    const key = aiMetadataKeyOf(fieldName);
    const alias = SECURITY_FIELD_BY_AI_METADATA_KEY[key] ?? key;
    names.add(key);
    names.add(alias);
    (REFERENCE_FIELD_SYNONYMS_BY_ALIAS[alias] ?? []).forEach(synonym => names.add(synonym));
  });
  return names;
};
